import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import type {
  ChatAppResponse,
  ChatMessageModel,
  ChatSessionModel,
  ResponseContext,
} from "@/api/models"

import { acceptTitleSuggestion, getMessagesApi } from "@/api/api"
import {
  createTenderChatSession,
  deleteTenderChatSession,
  listTenderChatSessionIds,
} from "@/api/tender-api"
import { SettingsService } from "@/services/settings-service"

import { TENDER_QUERY_KEYS } from "./use-tender-cases"

export const TENDER_CHAT_SESSION_KEYS = {
  sessions: (caseId: string) => ["tender-chat-sessions", caseId] as const,
  messages: (sessionId: string) => ["tender-chat-messages", sessionId] as const,
}

export function useTenderChatSessions(caseId: string | undefined) {
  return useQuery({
    queryKey: TENDER_CHAT_SESSION_KEYS.sessions(caseId ?? ""),
    queryFn: () => listTenderChatSessionIds(caseId!),
    enabled: !!caseId,
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    select: (sessions: ChatSessionModel[]) =>
      sessions
        .filter((s): s is NonNullable<ChatSessionModel> => s != null)
        .sort((a, b) => new Date(b.updated ?? 0).getTime() - new Date(a.updated ?? 0).getTime()),
  })
}

export function useCreateTenderChatSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { caseId: string; runId?: string }) =>
      createTenderChatSession(params.caseId, params.runId),
    onSuccess: (_session, params) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_CHAT_SESSION_KEYS.sessions(params.caseId),
      })
    },
    // Starting a chat auto-joins open viewers as participants server-side
    // BEFORE the session is created, so a session-storage failure can still
    // have flipped the caller's role — refresh the case on settle, not
    // success. The case page watches the access source and marks the
    // participant list (and other access-dependent caches) stale on the flip.
    onSettled: (_session, _error, params) => {
      void queryClient.invalidateQueries({ queryKey: TENDER_QUERY_KEYS.case(params.caseId) })
    },
  })
}

export function useTenderChatMessages(sessionId: string | undefined) {
  const token = SettingsService.getInstance().token

  return useQuery({
    queryKey: TENDER_CHAT_SESSION_KEYS.messages(sessionId ?? ""),
    queryFn: () => getMessagesApi(sessionId!, token),
    enabled: !!sessionId,
    staleTime: 1000 * 60,
    gcTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  })
}

export function useDeleteTenderChatSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { sessionId: string; caseId: string }) =>
      deleteTenderChatSession(params.caseId, params.sessionId),
    onSuccess: (_data, params) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_CHAT_SESSION_KEYS.sessions(params.caseId),
      })
    },
  })
}

export function useRenameTenderChatSession() {
  const queryClient = useQueryClient()
  const token = SettingsService.getInstance().token

  return useMutation({
    mutationFn: (params: { sessionId: string; title: string; caseId: string }) =>
      acceptTitleSuggestion(params.sessionId, params.title, token),
    onSuccess: (_data, params) => {
      void queryClient.invalidateQueries({
        queryKey: TENDER_CHAT_SESSION_KEYS.sessions(params.caseId),
      })
    },
  })
}

/**
 * Transforms stored ChatMessageModel[] into flat user/assistant pairs.
 * Backend stores each message as JSON `[userQuery, responseObject]`.
 */
/**
 * Strip the <tender_context>...</tender_context> wrapper that buildTenderMessage
 * prepends before the actual user prompt. When loaded from storage we only want
 * to display the bare prompt the user typed.
 */
function stripTenderContext(message: string): string {
  const withoutBlock = message.replace(/<tender_context>[\s\S]*?<\/tender_context>\s*/g, "")
  return withoutBlock.trim()
}

const EMPTY_CONTEXT: ResponseContext = {
  data_points: { text: [] },
  followup_questions: null,
  thoughts: [],
}

export function transformStoredMessages(
  storedMessages: ChatMessageModel[]
): Array<{ id: string; role: "user" | "assistant"; content: string; answer?: ChatAppResponse }> {
  const result: Array<{
    id: string
    role: "user" | "assistant"
    content: string
    answer?: ChatAppResponse
  }> = []

  for (const msg of storedMessages) {
    try {
      const raw: unknown = msg.content
      if (!Array.isArray(raw) || raw.length < 2) continue

      const userQuery = raw[0] as string
      const responseObj = raw[1] as Record<string, unknown> | null
      if (typeof userQuery === "string" && userQuery.length > 0) {
        result.push({ id: `${msg.id}-user`, role: "user", content: stripTenderContext(userQuery) })
      }
      if (
        responseObj != null &&
        typeof responseObj === "object" &&
        typeof responseObj.content === "string"
      ) {
        const context = (responseObj.context as ResponseContext | undefined) ?? EMPTY_CONTEXT
        const answer: ChatAppResponse = {
          content: responseObj.content,
          context,
          session_state: null,
          completed: true,
        }
        result.push({
          id: `${msg.id}-assistant`,
          role: "assistant",
          content: responseObj.content,
          answer,
        })
      }
    } catch {
      // Skip malformed messages
    }
  }

  return result
}
